'use client';

import type { PaginatedProduct, Swatches } from '@dealerportal/common';
import { logger } from '@dealerportal/core';
import { useEffect, useRef, useState } from 'react';
import { useCatalogContext, useRequireCiC } from '../Contexts/CatalogContext';
import { useCiCSearch } from './useCiCSearch';

export function useVariantLoader(product: PaginatedProduct) {
  const { catalogVersionId, loaded } = useCatalogContext();
  const { getSwatchesForProduct } = useCiCSearch();
  const [swatches, setSwatches] = useState<Swatches[]>(product.swatches ?? []);
  const [colorCount, setColorCount] = useState(product.colorCount ?? 1);
  const ref = useRef<HTMLDivElement | null>(null);
  const loadedRef = useRef(false);
  const needsSwatches =
    !product.swatches?.length &&
    !product.isSpreadColor &&
    !!catalogVersionId &&
    !!product.id;
  useRequireCiC(needsSwatches);

  useEffect(() => {
    if (product.swatches?.length) {
      setSwatches(product.swatches);
      setColorCount(product.colorCount ?? product.swatches.length);
      loadedRef.current = true;
    }
  }, [product]);

  useEffect(() => {
    if (
      !ref.current ||
      loadedRef.current ||
      !loaded ||
      product.isSpreadColor ||
      !catalogVersionId ||
      !product.id
    )
      return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !loadedRef.current) {
          loadedRef.current = true;
          observer.disconnect();

          getSwatchesForProduct(product)
            .then((swatches) => {
              setSwatches(swatches);
              setColorCount(swatches.length);
            })
            .catch((error) => {
              logger
                .withError(error)
                .warn(`Could not load swatches for ${product.id}`);
            });
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [product.id, product.isSpreadColor, getSwatchesForProduct, loaded]);

  return { ref, swatches, colorCount };
}
